import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatHoursMinutes, parseMinutes } from "@/lib/duration";
import { formatTime, TIME_INCREMENTS } from "../types";

/** Free-text duration input ("45", "1h 30m", "1.5h") with live hint and quick-add chips. */
export function DurationField({
	id,
	text,
	onTextChange,
	disabled,
	autoFocus,
	emptyHint = "Minutes, or use h / m (1h 30m).",
}: {
	id: string;
	text: string;
	onTextChange: (text: string) => void;
	disabled?: boolean;
	autoFocus?: boolean;
	emptyHint?: string;
}) {
	const minutes = parseMinutes(text);
	const invalid = text.trim() !== "" && minutes == null;

	return (
		<div className="flex flex-col gap-1.5">
			<Input
				id={id}
				autoFocus={autoFocus}
				value={text}
				placeholder="e.g. 45 or 1h 30m"
				disabled={disabled}
				aria-invalid={invalid || undefined}
				aria-describedby={`${id}-hint`}
				onChange={(e) => onTextChange(e.target.value)}
				className={cn(
					"h-9",
					invalid && "border-danger focus-visible:ring-danger",
				)}
			/>
			<p
				id={`${id}-hint`}
				className={cn("text-[11px]", invalid ? "text-danger" : "text-muted")}
			>
				{invalid
					? "Use minutes (45) or hours (1h 30m, 1.5h)."
					: minutes
						? `= ${formatTime(minutes)}`
						: emptyHint}
			</p>
			<div className="flex flex-wrap gap-1.5">
				{TIME_INCREMENTS.map(({ label, delta }) => (
					<Button
						key={label}
						type="button"
						size="sm"
						variant="outline"
						className="px-2"
						disabled={disabled}
						onClick={() =>
							onTextChange(formatHoursMinutes((minutes ?? 0) + delta))
						}
					>
						{label}
					</Button>
				))}
				{text && (
					<Button
						type="button"
						size="sm"
						variant="ghost"
						className="px-2"
						disabled={disabled}
						onClick={() => onTextChange("")}
					>
						Clear
					</Button>
				)}
			</div>
		</div>
	);
}
